import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { PdfFormOrderService, PdfFormItem, PdfFormLoadParams, PdfFormSaveParams } from './pdf-form-order.service';
import { ReturnTypeProviderService, ReturnType, ReturnTypeLoadParams } from '../../shared/return-type-provider.service';
import { CreateDisclosureService, DSTTaxYear } from '../../../DST/statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { DSTSharedService } from '../../../DST/shared/dst-shared.service';
import { ShutdownDstAppService } from '../../../DST/admin/shutdown-dst-app/shutdown-dst-app.service';
import { GlobalService } from '../../../global/services/global.service';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { environment } from '../../../../environments/environment';
import { catchError, filter, finalize, of, switchMap, take } from 'rxjs';
import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../../DST/shared/utils/dst-tax-year-appkit.support';
import {
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-pdf-form-order',
    templateUrl: './pdf-form-order.component.html',
    styleUrls: ['./pdf-form-order.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PdfFormOrderComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Input('gtw-param') gtwInputParam!: string;

  // The scrolling element of the reorder list, used to keep moved rows in view.
  @ViewChild('reorderList', { read: ElementRef }) reorderList?: ElementRef<HTMLElement>;

  // Filter dropdowns
  taxYears: DSTTaxYear[] = [];
  returnTypes: ReturnType[] = [];
  selectedTaxYear: DSTTaxYear | undefined;
  selectedReturnType: ReturnType | undefined;
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = null;
  returnTypeItems: any[] = [];
  selectedReturnTypeItem: any = null;

  // PDF Forms list
  pdfForms: PdfFormItem[] = [];
  // Search text, matching subset and current selection. `p-orderList` owned all
  // three internally; the AppKit list is presentational, so the screen keeps them.
  filterTerm: string = '';
  visibleForms: PdfFormItem[] = [];
  selectedForms: PdfFormItem[] = [];
  // Announced by `ap-list` in an aria-live region after a reorder.
  dragStatus: string = '';
  loading: boolean = false;
  isSaving: boolean = false;
  loadingTaxYears: boolean = false; // Loading state for tax year dropdown
  loadingReturnTypes: boolean = false; // Loading state for return type dropdown
  hasUnsavedChanges: boolean = false; // Track unsaved changes from drag-and-drop

  // Parameters from AngularJS
  private inputParams: any;
  private baseURL: string = '';

  constructor(
    private pdfFormOrderService: PdfFormOrderService,
    private returnTypeProvider: ReturnTypeProviderService,
    public dstSharedService: DSTSharedService,
    private workspaceService: WorkspaceService,
    private appService: AppService,
    private shutdownDstAppService: ShutdownDstAppService,
    private createDisclosureService: CreateDisclosureService,
    private router: Router,
    private globalService: GlobalService
  ) {}

  ngOnInit(): void {
    // This screen ships its own filter toolbar (Tax Year + Return Type), so
    // the workspace-level `gtw-data-filters` row coming from the grid shell is
    // a duplicate and must not render. Hide it the moment the component is
    // created — waiting for `gtw-param` to arrive in `ngOnChanges` leaves a
    // gap during which the previous screen's filters remain visible.
    this.workspaceService.hideDataFilters.next(true);
    // Routed `/pdf-form-order` / `/admin/pdf-form-order` have no `[gtw-param]`;
    // legacy still had `PdfFormOrderCtrl` build `inputParam` before the CE. Wait
    // for startup (`USER_SETTINGS`, `SERVER_CONFIG`) then bootstrap like legacy.
    this.scheduleStandaloneRouteBootstrap();
  }

  ngOnDestroy(): void {
    this.workspaceService.hideDataFilters.next(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const gtw = changes['gtwInputParam'];
    // Run whenever CE inputs arrive (including first non-empty change); this screen
    // ships its own filter toolbar — hide duplicate workspace `gtw-data-filters` row.
    if (!gtw || gtw.currentValue == null || gtw.currentValue === '') {
      return;
    }
    try {
      const raw = gtw.currentValue;
      const param = typeof raw === 'string' ? safeJsonInput<any>(raw, null, 'pdf-form-order.parse1') : raw;
      this.applyInputParams(param);
    } catch (e) {
      if (e instanceof SyntaxError) {
        console.error('JSON parse Syntax Error' + e);
      }
      this.dstSharedService.showAlert('error', 'Missing gtw input params, please contact system administrator for assistance!');
    }
  }

  /** Shared by `ngOnChanges` (CE / AngularJS shell) and standalone route bootstrap. */
  private applyInputParams(param: any): void {
    this.inputParams = param;

    this.dstSharedService.setClientKey(param.clientKey);
    this.dstSharedService.setBaseURLs(param.baseURLs);
    const roleLevel = Number.parseInt(String(param.roleLevel ?? '0'), 10);
    this.dstSharedService.setDSTUser(param.ssoId, Number.isFinite(roleLevel) ? roleLevel : 0);
    this.dstSharedService.setTaxYearScenario(param.taxYear, param.scenario);

    this.pdfFormOrderService.setBaseURL(param.baseURLs.api);

    this.returnTypeProvider.setClientKey(Number.parseInt(String(param.clientKey), 10));
    this.returnTypeProvider.setBaseURL(param.baseURLs.api);

    this.baseURL = param.baseURLs.api;

    this.workspaceService.hideDataFilters.next(true);

    this.loadInitialData();
  }

  /**
   * Standalone admin/top-level routes use this component directly — there is no
   * `[gtw-param]` binding, so `ngOnChanges` never runs. Rebuild the same object
   * legacy `PdfFormOrderCtrl` passed into `gtw-pdf-form-order-ce` (`PdfFormOrderController.js`).
   */
  private scheduleStandaloneRouteBootstrap(): void {
    const run = () => this.bootstrapIfStandalonePdfFormOrderRoute();
    this.appService.navItem$
      .pipe(
        filter((v) => v === 1),
        take(1)
      )
      .subscribe(() => setTimeout(run, 0));
  }

  private bootstrapIfStandalonePdfFormOrderRoute(): void {
    if (this.inputParams) {
      return;
    }
    const url = this.router.url || '';
    if (!url.includes('pdf-form-order')) {
      return;
    }
    const us = this.appService.USER_SETTINGS?.user;
    if (!us?.client_key) {
      return;
    }
    const gp = this.globalService.global?.globalParams || {};
    const latestTaxYear = this.extractLatestTaxYearValue() ?? String(gp.tax_year ?? '');

    const dstUsers = this.appService.SERVER_CONFIG?.configSettings?.dst_pdf_gen_users;
    const s3uploadButtonAccessUsers =
      typeof dstUsers === 'string' && dstUsers.length
        ? dstUsers.split(',')
        : [];

    const param: any = {
      clientKey: us.client_key,
      baseURLs: {
        api: environment.base_url,
        admin: environment.admin_api_url,
        custom: environment.custom_api_url,
        pdfEngine: environment.pdf_engine_api_url,
        bulkPdfEngine: environment.bulk_pdf_engine_api_url,
      },
      ssoId: us.sso_id,
      roleLevel: String(us.user_type ?? us.access_level ?? '0'),
      systemLockStatus: '1',
      taxYear: gp.tax_year,
      scenario: gp.scenario,
      latestTaxYear,
      s3uploadButtonAccessUsers,
      screenKey: String(gp.screen_key ?? ''),
      jcdKey: String(gp.jcd_key ?? ''),
      jcdTaKey: String(gp.jcd_ta_key ?? ''),
      activityKey: String(gp.activity_key ?? ''),
      screenType: String(gp.screen_type ?? 'grid'),
      isLocked: String(gp.is_locked ?? 'N'),
      issueId: gp.issue_id ?? '',
      issueShortDesc: gp.issue_short_desc ?? '',
      issueIdForTxSave: gp.issue_id_for_tx_save ?? '',
      scenarioCode: gp.scenarioCode ?? '',
      is_issue_key_enabled: gp.is_issue_key_enabled ?? '',
    };

    this.maybeEnrichScenarioFromGlobals(param);
    this.applyInputParams(param);
  }

  private extractLatestTaxYearValue(): string | undefined {
    const raw = this.appService.TAX_YEARS;
    if (!raw) {
      return undefined;
    }
    let items: any[] = [];
    if (Array.isArray(raw)) {
      items = raw;
    } else if (typeof raw === 'object') {
      items = ([] as any[]).concat(...Object.values(raw));
    }
    const nums = items
      .map((i: any) => Number(i?.value ?? i?.label))
      .filter((n: number) => Number.isFinite(n));
    if (!nums.length) {
      return undefined;
    }
    return String(Math.max(...nums));
  }

  /** Mirrors `PdfFormOrderController.js` scenario / issue-key enrichment from `GlobalService.inputs`. */
  private maybeEnrichScenarioFromGlobals(param: any): void {
    const tyInput = this.globalService.global?.inputs?.tax_year as any;
    if (!tyInput?.values?.length) {
      return;
    }
    const needScenarioCode =
      !param.scenarioCode || String(param.scenarioCode) === 'undefined';
    const needIssue =
      param.is_issue_key_enabled == null ||
      String(param.is_issue_key_enabled) === 'undefined';
    if (!needScenarioCode && !needIssue) {
      return;
    }
    const taxYear = param.taxYear ?? this.globalService.global?.globalParams?.tax_year;
    const scenario = param.scenario ?? this.globalService.global?.globalParams?.scenario;
    const yearObj = tyInput.values.find((i: any) => String(i.value) === String(taxYear));
    const found = yearObj?.scenarios?.find((s: any) => String(s.scenario) === String(scenario));
    if (!found) {
      return;
    }
    if (needScenarioCode && found.scenario_type_code) {
      param.scenarioCode = found.scenario_type_code;
    }
    if (needIssue && found.is_issue_key_enabled != null) {
      param.is_issue_key_enabled = found.is_issue_key_enabled;
    }
  }

  private requestedTaxYearToken(): string | undefined {
    if (!this.inputParams) {
      return undefined;
    }
    const p = this.inputParams;
    const raw = p.taxYear ?? p.tax_year ?? p.TAX_YEAR;
    if (raw == null || raw === '') {
      return undefined;
    }
    return String(raw);
  }

  private requestedReturnTypeToken(): string {
    if (!this.inputParams) {
      return '1120';
    }
    const p = this.inputParams;
    const raw = p.returnType ?? p.return_type ?? p.RETURN_TYPE;
    if (raw == null || raw === '') {
      return '1120';
    }
    return String(raw);
  }

  private pickTaxYear(taxYears: DSTTaxYear[]): DSTTaxYear | undefined {
    if (!taxYears.length) {
      return undefined;
    }
    const token = this.requestedTaxYearToken();
    if (token) {
      const match = taxYears.find((ty) => String(ty.text) === token);
      if (match) {
        return match;
      }
    }
    return taxYears[0];
  }

  private pickReturnType(returnTypes: ReturnType[]): ReturnType | undefined {
    if (!returnTypes.length) {
      return undefined;
    }
    const wanted = this.requestedReturnTypeToken();
    const byToken = returnTypes.find((rt) => rt.value === wanted || rt.label === wanted);
    if (byToken) {
      return byToken;
    }
    const by1120 = returnTypes.find((rt) => rt.value === '1120');
    if (by1120) {
      return by1120;
    }
    return returnTypes[0];
  }

  /**
   * Legacy network sequence when PDF Form Order loads (`PdfFormOrderController.js`):
   * 1) `SystemLockUnlockFactory.getDSTSystemLockUnlockStatus` → `loadJsonObject?action_id=32639`
   * 2) CE mounts; tax years → `loadJsonObject?action_id=32172` (`CreateDisclosureService.getTaxYears`)
   * 3) Return types → `loadJsonObject?action_id=33111` (`ReturnTypeProviderService.getReturnTypes`)
   */
  private loadInitialData(): void {
    if (!this.baseURL) {
      return;
    }
    this.loadingTaxYears = true;

    this.shutdownDstAppService.getSystemLockUnlockStatus(this.baseURL).pipe(
      catchError((err) => {
        console.warn('[PdfFormOrder] DST system lock (32639) failed:', err);
        return of(null);
      }),
      switchMap(() =>
        this.createDisclosureService.getTaxYears().pipe(
          catchError((err) => {
            console.error('[PdfFormOrder] Tax years (32172) failed:', err);
            this.dstSharedService.showAlert(
              'warning',
              'Tax-year service (32172) unavailable; using workspace tax years.'
            );
            return of([] as DSTTaxYear[]);
          })
        )
      ),
      finalize(() => {
        this.loadingTaxYears = false;
      }), takeUntilDestroyed(this.destroyRef)
    ).subscribe((rows: DSTTaxYear[]) => {
      const normalized = this.normalize32172TaxYears(rows);
      if (normalized.length) {
        this.taxYears = normalized;
        this.selectedTaxYear = this.pickTaxYear(this.taxYears);
        this.refreshTaxYearDropdownItems();
        if (this.selectedTaxYear) {
          this.loadReturnTypes();
        }
      } else {
        this.loadTaxYearsFromAppServiceFallback();
      }
    });
  }

  /** Map `action_id=32172` jsonObject rows to `DSTTaxYear` used by this screen. */
  private normalize32172TaxYears(rows: DSTTaxYear[] | any[]): DSTTaxYear[] {
    if (!Array.isArray(rows) || !rows.length) {
      return [];
    }
    return rows.map((item: any) => {
      const rawText =
        item?.text ?? item?.TAX_YEAR ?? item?.VALUE ?? item?.value ?? item?.label;
      const num = Number(rawText);
      const text: any = Number.isFinite(num) ? num : rawText;
      const idRaw = item?.id ?? item?.ID ?? text;
      const idNum = Number(idRaw);
      return {
        text,
        id: Number.isFinite(idNum) ? idNum : text,
        CURRENTFLAG: String(item?.CURRENTFLAG ?? ''),
        FILEDFLAG: String(item?.FILEDFLAG ?? ''),
      } as DSTTaxYear;
    });
  }

  /**
   * Fallback when 32172 returns no rows: same flattening as global `TAX_YEARS`
   * (`PdfFormOrderController.js` still injects `TAX_YEARS` for `latestTaxYear` in params).
   */
  private loadTaxYearsFromAppServiceFallback(): void {
    this.loadingTaxYears = true;

    const raw = this.appService.TAX_YEARS;
    if (!raw) {
      this.taxYears = [];
      this.loadingTaxYears = false;
      this.dstSharedService.showAlert('error', 'Tax years are not yet loaded. Please refresh and try again.');
      return;
    }

    let items: any[] = [];
    if (Array.isArray(raw)) {
      items = raw;
    } else if (typeof raw === 'object') {
      items = ([] as any[]).concat(...Object.values(raw));
    }

    const seen = new Set<string>();
    this.taxYears = items
      .filter((item: any) => {
        const v = String(item?.value ?? item?.label ?? item ?? '');
        if (!v || seen.has(v)) return false;
        seen.add(v);
        return true;
      })
      .map((item: any) => {
        const rawVal = item?.value ?? item?.label ?? item;
        const num = Number(rawVal);
        const text: any = Number.isFinite(num) ? num : rawVal;
        return {
          text,
          id: text,
          CURRENTFLAG: '',
          FILEDFLAG: '',
        } as DSTTaxYear;
      });

    this.loadingTaxYears = false;

    this.selectedTaxYear = this.pickTaxYear(this.taxYears);
    this.refreshTaxYearDropdownItems();

    if (this.selectedTaxYear) {
      this.loadReturnTypes();
    }
  }

  onTaxYearChange(): void {
    if (this.selectedTaxYear) {
      this.selectedReturnType = undefined;
      this.selectedReturnTypeItem = null;
      this.pdfForms = [];
      this.refreshVisibleForms();
      this.loadReturnTypes();
    }
  }

  get emptyMessage(): string {
    if (this.loading) {
      return 'Loading...';
    }
    if (this.filterTerm && this.pdfForms.length) {
      return 'No forms match your search.';
    }
    return 'Please select filters and click Filter button.';
  }

  onTaxYearDropdownChange(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.selectedTaxYear = matched;
      this.onTaxYearChange();
    }
  }

  get hasSelection(): boolean {
    return this.selectedForms.length > 0;
  }

  isSelected(form: PdfFormItem): boolean {
    return this.selectedForms.indexOf(form) > -1;
  }

  /**
   * Rebuilds the rendered subset and drops selections the search term has hidden.
   * Keeping a hidden row selected would let the move buttons reorder something the
   * user cannot see, and Save would then persist that invisible change.
   */
  refreshVisibleForms(): void {
    const term = (this.filterTerm || '').trim().toLowerCase();
    this.visibleForms = term
      ? this.pdfForms.filter((form) => (form.FORM || '').toLowerCase().includes(term))
      : [...this.pdfForms];
    this.selectedForms = this.selectedForms.filter((form) => this.visibleForms.indexOf(form) > -1);
  }

  /** Ctrl/Cmd toggles, Shift extends from the last selected row, plain click replaces. */
  onItemClick(event: Event, form: PdfFormItem): void {
    const modifierEvent = event as MouseEvent | KeyboardEvent;
    if (modifierEvent.ctrlKey || modifierEvent.metaKey) {
      const index = this.selectedForms.indexOf(form);
      if (index > -1) {
        this.selectedForms.splice(index, 1);
      } else {
        this.selectedForms.push(form);
      }
      return;
    }

    if (modifierEvent.shiftKey && this.selectedForms.length) {
      const anchor = this.visibleForms.indexOf(this.selectedForms[this.selectedForms.length - 1]);
      const target = this.visibleForms.indexOf(form);
      if (anchor > -1 && target > -1) {
        const start = Math.min(anchor, target);
        const end = Math.max(anchor, target);
        this.selectedForms = this.visibleForms.slice(start, end + 1);
        return;
      }
    }

    this.selectedForms = [form];
  }

  onDrop(event: CdkDragDrop<PdfFormItem[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    // Indices are positions in the rendered (possibly filtered) subset, so they
    // have to be translated back to positions in the full list before moving.
    const moved = this.visibleForms[event.previousIndex];
    const target = this.visibleForms[event.currentIndex];
    const from = this.pdfForms.indexOf(moved);
    const to = this.pdfForms.indexOf(target);
    if (from < 0 || to < 0) {
      return;
    }

    moveItemInArray(this.pdfForms, from, to);
    this.dragStatus = `${moved.FORM} moved to position ${to + 1} of ${this.pdfForms.length}`;
    this.onReorder();
  }

  /**
   * Rows already stacked against the top cannot move, but the rest of the
   * selection still shifts up — stopping at the first blocked row would leave
   * a non-contiguous selection stuck, and moving it anyway would shuffle a
   * pinned block apart.
   */
  moveUp(): void {
    let pinned = 0;
    let moved = false;
    for (const form of this.selectionInListOrder()) {
      const index = this.pdfForms.indexOf(form);
      if (index === pinned) {
        pinned++;
        continue;
      }
      moveItemInArray(this.pdfForms, index, index - 1);
      moved = true;
    }
    if (!moved) {
      return;
    }
    this.onReorder();
    this.announceSelection('first');
    this.scrollSelectionIntoView('first');
  }

  moveTop(): void {
    const selection = this.selectionInListOrder().reverse();
    let moved = false;
    for (const form of selection) {
      const index = this.pdfForms.indexOf(form);
      if (index === 0) {
        break;
      }
      moveItemInArray(this.pdfForms, index, 0);
      moved = true;
    }
    if (!moved) {
      return;
    }
    this.onReorder();
    this.announceSelection('first');
    this.scrollSelectionIntoView('first');
  }

  /** Mirror of `moveUp`, with the pinned rows counted from the bottom. */
  moveDown(): void {
    let pinned = this.pdfForms.length - 1;
    let moved = false;
    for (const form of this.selectionInListOrder().reverse()) {
      const index = this.pdfForms.indexOf(form);
      if (index === pinned) {
        pinned--;
        continue;
      }
      moveItemInArray(this.pdfForms, index, index + 1);
      moved = true;
    }
    if (!moved) {
      return;
    }
    this.onReorder();
    this.announceSelection('last');
    this.scrollSelectionIntoView('last');
  }

  moveBottom(): void {
    const last = this.pdfForms.length - 1;
    let moved = false;
    for (const form of this.selectionInListOrder()) {
      const index = this.pdfForms.indexOf(form);
      if (index === last) {
        break;
      }
      moveItemInArray(this.pdfForms, index, last);
      moved = true;
    }
    if (!moved) {
      return;
    }
    this.onReorder();
    this.announceSelection('last');
    this.scrollSelectionIntoView('last');
  }

  /** Moves must run in list order, not in the order the rows were clicked. */
  private selectionInListOrder(): PdfFormItem[] {
    return [...this.selectedForms].sort(
      (a, b) => this.pdfForms.indexOf(a) - this.pdfForms.indexOf(b)
    );
  }

  /**
   * Feeds the live region `ap-list` renders from `dragStatus`, so the button
   * driven moves are announced the same way a drag is.
   */
  private announceSelection(edge: 'first' | 'last'): void {
    const selection = this.selectionInListOrder();
    if (!selection.length) {
      this.dragStatus = '';
      return;
    }
    const target = edge === 'first' ? selection[0] : selection[selection.length - 1];
    const position = this.pdfForms.indexOf(target) + 1;
    const others = selection.length > 1 ? `, ${selection.length - 1} more selected` : '';
    this.dragStatus = `${target.FORM} moved to position ${position} of ${this.pdfForms.length}${others}`;
  }

  /**
   * Follows the selection after a move: upward moves track the topmost
   * selected row, downward moves the bottom one. Deferred so the rows have
   * been re-rendered in their new order before the offsets are read.
   */
  private scrollSelectionIntoView(edge: 'first' | 'last'): void {
    const selection = this.selectionInListOrder();
    if (!selection.length) {
      return;
    }
    const target = edge === 'first' ? selection[0] : selection[selection.length - 1];
    const index = this.visibleForms.indexOf(target);
    if (index < 0) {
      return;
    }

    setTimeout(() => {
      const rows = this.reorderList?.nativeElement.querySelectorAll('.ap-list-item');
      const row = rows?.[index] as HTMLElement | undefined;
      row?.scrollIntoView({ block: 'nearest' });
    });
  }
  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  onReturnTypeDropdownChange(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedReturnTypeItem = selected;
    this.selectedReturnType = this.returnTypes.find((rt) => rt.value === selected?.value || rt.label === selected?.label);
  }

  onReturnTypeSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedReturnTypeItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  private refreshTaxYearDropdownItems(): void {
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.selectedTaxYear);
  }

  private refreshReturnTypeDropdownItems(): void {
    this.returnTypeItems = mapToAppkitDropdownItems(this.returnTypes, 'label', 'value');
    this.selectedReturnTypeItem = this.returnTypeItems.find(
      (item) => item.value === this.selectedReturnType?.value
    ) || null;
  }

  private loadReturnTypes(): void {
    if (!this.inputParams || !this.selectedTaxYear) {
      return;
    }

    this.loadingReturnTypes = true;

    const params: ReturnTypeLoadParams = {
      taxYear: this.selectedTaxYear.text.toString(),
      screenKey: this.inputParams.screenKey,
      scenario: this.inputParams.scenario,
      jcdKey: this.inputParams.jcdKey,
      jcdTaKey: this.inputParams.jcdTaKey,
      activityKey: this.inputParams.activityKey,
      screenType: this.inputParams.screenType,
      isLocked: this.inputParams.isLocked,
      issueId: this.inputParams.issueId || '',
      issueShortDesc: this.inputParams.issueShortDesc || '',
      issueIdForTxSave: this.inputParams.issueIdForTxSave || ''
    };

    this.returnTypeProvider.getReturnTypes(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (returnTypes: ReturnType[]) => {
        this.returnTypes = returnTypes;
        this.selectedReturnType = this.pickReturnType(this.returnTypes);
        this.refreshReturnTypeDropdownItems();
        this.loadingReturnTypes = false;
      },
      (error: HttpErrorResponse) => {
        console.error('Error loading return types:', error);
        this.dstSharedService.showAlert('error', 'Failed to load return types');
        this.loadingReturnTypes = false;
      }
    );
  }

  onFilterClick(): void {
    if (!this.selectedTaxYear || !this.selectedReturnType) {
      this.dstSharedService.showAlert('warning', 'Please select both Tax Year and Return Type');
      return;
    }

    if (this.hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Loading new data will discard them. Continue?'
      );
      
      if (!confirmed) {
        return;
      }
      
      this.hasUnsavedChanges = false;
    }

    this.loadPdfForms();
  }

  private loadPdfForms(silent: boolean = false): void {
    this.loading = true;
    this.pdfForms = [];
    this.dragStatus = '';
    this.refreshVisibleForms();

    const params: PdfFormLoadParams = {
      taxYear: this.selectedTaxYear!.text.toString(),
      returnType: this.selectedReturnType!.value,
      screenKey: this.inputParams.screenKey,
      scenario: this.inputParams.scenario,
      jcdKey: this.inputParams.jcdKey,
      jcdTaKey: this.inputParams.jcdTaKey,
      activityKey: this.inputParams.activityKey,
      screenType: this.inputParams.screenType,
      isLocked: this.inputParams.isLocked,
      scenarioCode: this.inputParams.scenarioCode || this.inputParams.scenario_code,
      isIssueKeyEnabled: this.inputParams.isIssueKeyEnabled || this.inputParams.is_issue_key_enabled,
      issueId: this.inputParams.issueId || '',
      issueShortDesc: this.inputParams.issueShortDesc || '',
      issueIdForTxSave: this.inputParams.issueIdForTxSave || ''
    };

    this.pdfFormOrderService.loadPdfForms(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        this.pdfForms = response.data.sort((a, b) => {
          if (!a.FORM_ORDER && !b.FORM_ORDER) return 0;
          if (!a.FORM_ORDER) return 1;
          if (!b.FORM_ORDER) return -1;
          return a.FORM_ORDER - b.FORM_ORDER;
        });
        this.refreshVisibleForms();
        
        if (!silent) {
          this.dstSharedService.showAlert('success', `Loaded ${this.pdfForms.length} PDF forms`);
        }
        
        this.loading = false;
      },
      (error) => {
        console.error('Error loading PDF forms:', error);
        if (!silent) {
          this.dstSharedService.showAlert('error', 'Failed to load PDF forms. Please try again.');
        }
        this.loading = false;
      }
    );
  }

  onReorder(): void {
    this.pdfForms.forEach((form, index) => {
      form.FORM_ORDER = index + 1;
    });

    this.refreshVisibleForms();
    this.hasUnsavedChanges = true;
  }

  onSaveClick(): void {
    if (!this.hasUnsavedChanges) {
      return;
    }

    const newOrder = this.pdfForms.map((form, index) => ({
      FORM: form.FORM,
      FORM_ORDER: index + 1
    }));

    this.saveNewOrder(newOrder);
  }

  onSortAscending(): void {
    this.pdfForms = [...this.pdfForms].sort((a, b) => 
      a.FORM.localeCompare(b.FORM)
    );

    this.onReorder();
  }

  onSortDescending(): void {
    this.pdfForms = [...this.pdfForms].sort((a, b) => 
      b.FORM.localeCompare(a.FORM)
    );

    this.onReorder();
  }

  private saveNewOrder(newOrder: any[]): void {
    this.isSaving = true;
    const startTime = Date.now();

    const formOrderData = newOrder.map(item => ({
      FORM: item.FORM,
      FORM_ORDER: item.FORM_ORDER
    }));

    const params: PdfFormSaveParams = {
      tax_year: this.selectedTaxYear!.text.toString(),
      return_type: this.selectedReturnType!.value,
      screen_key: this.inputParams.screenKey,
      scenario: this.inputParams.scenario,
      jcd_key: this.inputParams.jcdKey,
      jcd_ta_key: this.inputParams.jcdTaKey,
      activity_key: this.inputParams.activityKey,
      screen_type: this.inputParams.screenType,
      is_locked: this.inputParams.isLocked,
      scenarioCode: this.inputParams.scenarioCode,
      is_issue_key_enabled: this.inputParams.is_issue_key_enabled
    };

    this.pdfFormOrderService.savePdfFormOrder(params, formOrderData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.dstSharedService.showAlert('success', 'PDF Form order updated successfully');
          this.hasUnsavedChanges = false;
          this.ensureMinimumLoadingTimeAndReload(startTime, true);
        } else {
          const errorMsg = response.errorMessage || 'Failed to save PDF form order';
          this.dstSharedService.showAlert('error', errorMsg);
          this.ensureMinimumLoadingTime(startTime);
        }
      },
      (error) => {
        this.dstSharedService.showAlert('error', 'Failed to save PDF form order. Please try again.');
        this.ensureMinimumLoadingTime(startTime);
      }
    );
  }

  private ensureMinimumLoadingTime(startTime: number): void {
    const elapsedTime = Date.now() - startTime;
    const minimumTime = 1000;
    
    if (elapsedTime < minimumTime) {
      setTimeout(() => {
        this.isSaving = false;
      }, minimumTime - elapsedTime);
    } else {
      this.isSaving = false;
    }
  }

  private ensureMinimumLoadingTimeAndReload(startTime: number, silent: boolean = true): void {
    const elapsedTime = Date.now() - startTime;
    const minimumTime = 1000;
    
    if (elapsedTime < minimumTime) {
      setTimeout(() => {
        this.isSaving = false;
        this.loadPdfForms(silent);
      }, minimumTime - elapsedTime);
    } else {
      this.isSaving = false;
      this.loadPdfForms(silent);
    }
  }

}
